// Jobs: so many leaflets handed to one person for one zone. The Jobs tab lists
// them; a job opens to its rounds, progress and route sheet. JobModal gives
// out leaflets (or edits a job), LogRoundModal enters a round for someone who
// sent their screenshot another way.
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Select, Textarea, Checkbox, ModalShell, Pill } from '../lib/ui';
import { useLeafletMap, fitPoints } from '../lib/leafletMap';
import { formatDay, formatGBP, todayYmd, ymd } from '../lib/trade';
import {
  payFor, jobProgress, zoneState, polygonsOverlap, formatCount, JOB_STATUS, PERSON_STATUS,
} from '../lib/leaflets';
import { byId, PersonTag, JobPill, JobProgress, ZonesLayer, Facts } from './LeafletsCommon';
import { RoundCard } from './LeafletsRounds';
import { Label, errorText } from './TradeCommon';

const FILTERS = [
  { key: 'active', label: 'In progress' },
  { key: 'done', label: 'Done' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

export function JobsList({ data, onOpen }) {
  const [filter, setFilter] = useState('active');
  const [q, setQ] = useState('');
  const people = byId(data.distributors);
  const zones = byId(data.zones);
  const term = q.trim().toLowerCase();
  const count = (key) => (key === 'all' ? data.jobs.length : data.jobs.filter(j => j.status === key).length);
  const shown = data.jobs
    .filter(j => filter === 'all' || j.status === filter)
    .filter(j => !term || [people[j.distributor]?.name, zones[j.zone]?.name, zones[j.zone]?.area, j.leaflet]
      .some(v => (v || '').toLowerCase().includes(term)));

  return (
    <>
      <div className="trade-toolbar">
        <div className="trade-seg">
          {FILTERS.map(f => (
            <button key={f.key} type="button" className={filter === f.key ? 'is-on' : ''} onClick={() => setFilter(f.key)}>
              {f.label}<span className="n">{count(f.key)}</span>
            </button>
          ))}
        </div>
        <Input placeholder="Search people, zones…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      {shown.length ? (
        <div className="ui-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="trade-table">
            <thead>
              <tr><th>Who</th><th>Zone</th><th className="num">Leaflets</th><th>Progress</th><th>Given</th><th>Finish by</th><th>Status</th></tr>
            </thead>
            <tbody>
              {shown.map(job => {
                const zone = zones[job.zone];
                const waiting = job.runs.filter(r => r.status === 'submitted').length;
                return (
                  <tr key={job._id} onClick={() => onOpen(job._id)}>
                    <td><PersonTag person={people[job.distributor]} />{job.kind === 'trial' && <span className="sub">Trial</span>}</td>
                    <td>{zone?.name || 'Deleted zone'}{zone?.area && <span className="sub">{zone.area}</span>}</td>
                    <td className="num">{formatCount(job.leaflets)}</td>
                    <td style={{ minWidth: 180 }}><JobProgress job={job} /></td>
                    <td>{formatDay(job.givenOn)}</td>
                    <td>{job.dueBy ? formatDay(job.dueBy) : <span className="lf-muted">Any time</span>}</td>
                    <td><JobPill job={job} />{waiting > 0 && <span className="sub">{waiting} to check</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="trade-empty">
          <strong>{data.jobs.length ? 'No jobs here' : 'No jobs yet'}</strong>
          {data.jobs.length ? 'Try another filter.' : 'Give someone a bundle of leaflets and a zone, and the job shows up here.'}
        </div>
      )}
    </>
  );
}

function ZoneMiniMap({ zone }) {
  const ref = useRef(null);
  const { map, error } = useLeafletMap(ref, { scrollWheelZoom: false });
  const items = useMemo(() => [{ zone, colour: '#2e7d32', fill: 0.15 }], [zone]);
  useEffect(() => { if (map) fitPoints(map, [...zone.polygon, ...(zone.route || [])]); }, [map, zone]);
  return (
    <div className="lf-map-wrap">
      <div ref={ref} className="lf-map lf-map--mini" />
      {!map && <div className="lf-map-msg">{error || 'Loading the map…'}</div>}
      <ZonesLayer map={map} items={items} interactive={false} />
    </div>
  );
}

export function JobDetail({ job, data, actions, putJob, dropJob, flash, onBack }) {
  const people = byId(data.distributors);
  const zone = data.zones.find(z => z._id === job.zone);
  const person = people[job.distributor];
  const [busy, setBusy] = useState('');
  const { approved, left } = jobProgress(job);
  const runs = [...job.runs].sort((a, b) => String(b.date).localeCompare(String(a.date)) || String(b.createdAt).localeCompare(String(a.createdAt)));

  const setStatus = async (status, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(status);
    try {
      const { data: res } = await api.patch(`/leaflets/admin/jobs/${job._id}`, { status });
      putJob(res.job);
      flash(`Marked ${JOB_STATUS[status].label.toLowerCase()}`);
    } catch (e) {
      flash(errorText(e), 'bad');
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this job? Only do this if it was set up by mistake.')) return;
    try {
      await api.delete(`/leaflets/admin/jobs/${job._id}`);
      dropJob(job._id);
      onBack();
      flash('Job deleted', 'info');
    } catch (e) {
      flash(errorText(e), 'bad');
    }
  };

  return (
    <>
      <Button size="sm" variant="muted" className="trade-back" onClick={onBack}>&larr; All jobs</Button>
      <div className="trade-hero">
        <div className="trade-hero__top">
          <div>
            <h2>{zone?.name || 'Deleted zone'}</h2>
            <div className="lf-row">
              <PersonTag person={person} onClick={() => actions.openPerson(job.distributor)} />
              <JobPill job={job} />
              {job.kind === 'trial' && <Pill tone="blue">Trial</Pill>}
            </div>
          </div>
          <div className="lf-row">
            {zone && <Button onClick={() => actions.print({ zone, job })}>Print route sheet</Button>}
            {job.status === 'active' && <Button variant="primary" onClick={() => actions.logRound(job)}>Log a round</Button>}
          </div>
        </div>
        <Facts items={[
          ['Leaflets', formatCount(job.leaflets)],
          job.leaflet && ['Leaflet', job.leaflet],
          ['Pay', `£${job.ratePer1000} per 1000 (${formatGBP(payFor(job.leaflets, job.ratePer1000))} for all)`],
          ['Given', formatDay(job.givenOn)],
          ['Finish by', job.dueBy ? formatDay(job.dueBy, { weekday: true }) : 'Any time'],
          zone?.homes && ['Homes in zone', `about ${formatCount(zone.homes)}`],
        ]} />
        <div className="lf-hero-progress">
          <JobProgress job={job} />
          {left > 0 && <span className="lf-muted">{formatCount(left)} still to go</span>}
        </div>
      </div>

      <div className="trade-detail">
        <div className="trade-detail__col">
          <div className="ui-card">
            <h3>Rounds</h3>
            {runs.length ? (
              <div className="lf-rounds">
                {runs.map(run => (
                  <RoundCard key={run._id} job={job} run={run} zone={zone} person={person} onJob={putJob} flash={flash} showWho={false} />
                ))}
              </div>
            ) : <p className="lf-muted">Nothing logged yet. Rounds appear here when {person?.name?.split(' ')[0] || 'they'} log them in the app.</p>}
          </div>
        </div>
        <div className="trade-detail__col">
          {zone && <div className="ui-card" style={{ padding: 0, overflow: 'hidden' }}><ZoneMiniMap zone={zone} /></div>}
          {(job.instructions || zone?.instructions) && (
            <div className="ui-card">
              <h3>Instructions</h3>
              <p className="lf-pre">{[zone?.instructions, job.instructions].filter(Boolean).join('\n\n')}</p>
            </div>
          )}
          {job.notes && <div className="ui-card"><h3>Notes</h3><p className="lf-pre">{job.notes}</p></div>}
          <div className="ui-card">
            <h3>Job</h3>
            <div className="lf-row">
              <Button size="sm" onClick={() => actions.editJob(job)}>Edit</Button>
              {job.status === 'active' && <Button size="sm" onClick={() => setStatus('done')} disabled={!!busy}>Mark done</Button>}
              {job.status !== 'active' && <Button size="sm" onClick={() => setStatus('active')} disabled={!!busy}>Reopen</Button>}
              {job.status === 'active' && (
                <Button size="sm" variant="danger" disabled={!!busy}
                  onClick={() => setStatus('cancelled', 'Cancel this job? They won\'t be able to log rounds on it.')}>Cancel job</Button>
              )}
              {!job.runs.length && <Button size="sm" variant="danger" onClick={remove}>Delete</Button>}
            </div>
            <p className="trade-form__hint" style={{ marginTop: 8 }}>
              {approved >= job.leaflets ? 'Every leaflet is delivered. ' : ''}
              A job marks itself done once its leaflets are all delivered, or a round saying the zone is finished is approved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

const QUICK_COUNTS = [500, 1000, 1500, 2000];

// Give out leaflets: { zone?, distributor? } to start a new job, or { job } to edit one.
export function JobModal({ start, data, today = todayYmd(), onClose, onSaved }) {
  const editing = start.job || null;
  const settings = data.settings || {};
  const people = data.distributors.filter(p => p.status !== 'left' || p._id === editing?.distributor);
  const zones = data.zones.filter(z => !z.archived || z._id === editing?.zone);
  const [f, setF] = useState(() => (editing ? {
    distributor: editing.distributor, zone: editing.zone, kind: editing.kind, leaflets: String(editing.leaflets),
    leaflet: editing.leaflet, ratePer1000: String(editing.ratePer1000), givenOn: ymd(editing.givenOn), dueBy: ymd(editing.dueBy),
    instructions: editing.instructions, notes: editing.notes,
  } : {
    distributor: start.distributor || '', zone: start.zone || '', kind: '', leaflets: '1000',
    leaflet: settings.leaflet || '', ratePer1000: '', givenOn: today, dueBy: '', instructions: '', notes: '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  const person = people.find(p => p._id === f.distributor);
  const zone = zones.find(z => z._id === f.zone);
  const kind = f.kind || (person?.status === 'trial' ? 'trial' : 'regular');
  const defaultRate = person?.ratePer1000 ?? settings.ratePer1000;
  const rate = f.ratePer1000 === '' ? defaultRate : Number(f.ratePer1000);
  const count = Number(f.leaflets) || 0;
  const designs = [...new Set([settings.leaflet, ...data.jobs.map(j => j.leaflet)].filter(Boolean))];

  // Warn before sending two people down the same streets
  const warnings = [];
  if (zone) {
    const others = data.jobs.filter(j => j.status === 'active' && j._id !== editing?._id && j.distributor !== f.distributor);
    for (const j of others) {
      const z = data.zones.find(x => x._id === j.zone);
      const who = data.distributors.find(p => p._id === j.distributor)?.name || 'Someone';
      if (j.zone === zone._id) warnings.push(`${who} already has ${zone.name}.`);
      else if (z && polygonsOverlap(zone.polygon, z.polygon)) warnings.push(`${zone.name} overlaps ${z.name}, which ${who} has right now.`);
    }
    if (zone.homes && count > zone.homes * 1.2) warnings.push(`That's more leaflets than the roughly ${formatCount(zone.homes)} homes in ${zone.name}.`);
    const st = zoneState(zone, data.jobs, today);
    if (st.state === 'covered') warnings.push(`${zone.name} was last delivered ${formatDay(st.last)}.`);
  }

  const save = async () => {
    if (!f.distributor) return setError('Choose who is delivering');
    if (!f.zone) return setError('Choose a zone');
    setSaving(true);
    setError('');
    const body = { ...f, kind };
    try {
      const { data: res } = editing
        ? await api.patch(`/leaflets/admin/jobs/${editing._id}`, body)
        : await api.post('/leaflets/admin/jobs', body);
      onSaved(res.job, !editing);
    } catch (e) {
      setError(errorText(e));
      setSaving(false);
    }
  };

  const zoneLabel = (z) => {
    const st = zoneState(z, data.jobs, today);
    const who = st.active.map(j => data.distributors.find(p => p._id === j.distributor)?.name.split(' ')[0]).filter(Boolean);
    const extra = st.state === 'assigned' ? ` (with ${who.join(' & ')})` : st.state === 'covered' ? ` (done ${formatDay(st.last)})` : '';
    return `${z.name}${z.area ? `, ${z.area}` : ''}${z.homes ? `, ~${formatCount(z.homes)} homes` : ''}${extra}`;
  };

  return (
    <ModalShell onClose={onClose} width={640} ariaLabel={editing ? 'Edit job' : 'Give out leaflets'}>
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>{editing ? 'Edit job' : 'Give out leaflets'}</h2>
      <div className="trade-form">
        <div className="inv-grid2">
          <div>
            <Label>Who</Label>
            <Select value={f.distributor} onChange={set('distributor')} disabled={!!editing?.runs.length}>
              <option value="">Choose…</option>
              {people.map(p => <option key={p._id} value={p._id}>{p.name} ({PERSON_STATUS[p.status]?.label})</option>)}
            </Select>
          </div>
          <div>
            <Label>Zone</Label>
            <Select value={f.zone} onChange={set('zone')}>
              <option value="">Choose…</option>
              {zones.map(z => <option key={z._id} value={z._id}>{zoneLabel(z)}</option>)}
            </Select>
          </div>
        </div>
        {person?.areas && <div className="trade-form__hint">{person.name.split(' ')[0]} said: {person.areas}</div>}

        <div className="inv-grid2">
          <div>
            <Label>Leaflets</Label>
            <Input type="number" min="1" step="1" value={f.leaflets} onChange={set('leaflets')} />
            <div className="trade-quick">
              {QUICK_COUNTS.map(n => <button key={n} type="button" onClick={() => setF(p => ({ ...p, leaflets: String(n) }))}>{formatCount(n)}</button>)}
            </div>
          </div>
          <div>
            <Label>Kind</Label>
            <div className="trade-seg">
              {[['trial', 'Trial'], ['regular', 'Regular']].map(([k, label]) => (
                <button key={k} type="button" className={kind === k ? 'is-on' : ''} onClick={() => setF(p => ({ ...p, kind: k }))}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="inv-grid2">
          <div>
            <Label hint="£ per 1000 delivered">Pay</Label>
            <div className="pl-price">
              <Input type="number" min="0" step="0.5" value={f.ratePer1000} onChange={set('ratePer1000')}
                placeholder={defaultRate !== null && defaultRate !== undefined ? String(defaultRate) : 'e.g. 40'} />
            </div>
            <div className="trade-form__hint">
              {rate !== null && rate !== undefined && !Number.isNaN(rate)
                ? `${formatGBP(payFor(count, rate))} if all ${formatCount(count)} go out`
                : 'No default pay set yet (Settings).'}
            </div>
          </div>
          <div>
            <Label hint="for the flier log">Leaflet</Label>
            <Input list="lf-designs" value={f.leaflet} onChange={set('leaflet')} placeholder="e.g. Evergreen" />
            <datalist id="lf-designs">{designs.map(d => <option key={d} value={d} />)}</datalist>
          </div>
        </div>

        <div className="inv-grid2">
          <div><Label>Given on</Label><Input type="date" value={f.givenOn} onChange={set('givenOn')} /></div>
          <div><Label hint="optional">Finish by</Label><Input type="date" value={f.dueBy} onChange={set('dueBy')} /></div>
        </div>
        <div><Label hint="shown in the app and printed, on top of the zone's own">Instructions</Label><Textarea rows={2} value={f.instructions} onChange={set('instructions')} /></div>
        <div><Label hint="only in Farm OS">Notes</Label><Textarea rows={2} value={f.notes} onChange={set('notes')} /></div>

        {warnings.length > 0 && (
          <div className="ui-banner ui-banner--warn"><ul className="lf-plain">{warnings.map(w => <li key={w}>{w}</li>)}</ul></div>
        )}
        {error && <div className="ui-banner ui-banner--bad">{error}</div>}
      </div>
      <div className="trade-form__actions">
        <Button variant="muted" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save' : 'Give out leaflets'}</Button>
      </div>
    </ModalShell>
  );
}

// A round entered from Farm OS, e.g. the screenshot came by WhatsApp. It's
// approved as it's saved.
export function LogRoundModal({ job, data, today = todayYmd(), onClose, onSaved }) {
  const person = data.distributors.find(p => p._id === job.distributor);
  const zone = data.zones.find(z => z._id === job.zone);
  const { left } = jobProgress(job);
  const [f, setF] = useState({ date: today, delivered: String(left || ''), distanceKm: '', minutes: '', notes: '', finished: false, pay: '' });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const computed = payFor(Number(f.delivered) || 0, job.ratePer1000);

  const save = async () => {
    setSaving(true);
    setError('');
    const form = new FormData();
    for (const [k, v] of Object.entries(f)) form.append(k, String(v));
    for (const file of files) form.append('photos', file);
    try {
      const { data: res } = await api.post(`/leaflets/admin/jobs/${job._id}/runs`, form);
      onSaved(res.job);
    } catch (e) {
      setError(errorText(e));
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={560} ariaLabel="Log a round">
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Log a round</h2>
      <p className="lf-muted" style={{ marginBottom: 14 }}>For {person?.name} in {zone?.name}. It's approved as you save it.</p>
      <div className="trade-form">
        <div className="inv-grid3">
          <div><Label>Date</Label><Input type="date" value={f.date} max={today} onChange={set('date')} /></div>
          <div><Label>Leaflets</Label><Input type="number" min="1" value={f.delivered} onChange={set('delivered')} /></div>
          <div>
            <Label hint={`£${job.ratePer1000}/1000`}>Pay</Label>
            <div className="pl-price"><Input value={f.pay} onChange={set('pay')} placeholder={computed.toFixed(2)} /></div>
          </div>
        </div>
        <div className="inv-grid2">
          <div><Label hint="optional">Distance (km)</Label><Input type="number" min="0" step="0.1" value={f.distanceKm} onChange={set('distanceKm')} /></div>
          <div><Label hint="optional">Time (minutes)</Label><Input type="number" min="0" step="1" value={f.minutes} onChange={set('minutes')} /></div>
        </div>
        <div>
          <Label hint="optional">Screenshots</Label>
          <input type="file" accept="image/*" multiple onChange={e => setFiles([...e.target.files].slice(0, 6))} />
          {files.length > 0 && <div className="trade-form__hint">{files.length} picture{files.length === 1 ? '' : 's'}</div>}
        </div>
        <div><Label hint="optional">Notes</Label><Textarea rows={2} value={f.notes} onChange={set('notes')} /></div>
        <Checkbox checked={f.finished} onChange={set('finished')} label="This finishes the job (zone done, or the leaflets ran out)" />
        {error && <div className="ui-banner ui-banner--bad">{error}</div>}
      </div>
      <div className="trade-form__actions">
        <Button variant="muted" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : `Save, ${formatGBP(f.pay === '' ? computed : Number(f.pay) || 0)}`}</Button>
      </div>
    </ModalShell>
  );
}
